import { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { bindActionCreators } from "redux";

import { Box, useDisclosure, useToast } from "@chakra-ui/react";
import {
  deleteFiles,
  deleteFilesReset,
  manageSharedDoc,
  resetManageSharedDoc,
} from "../actions/dsr-actions";

import "./styles/dsr-shared-docs-display.scss";
import PublicDsrSharedDocSignupPrompt from "../dsr-content-components/public-dsr/PublicDsrSharedDocSignupPrompt";
import { searchArrayofObjects, triggerLaunchdeckToast } from "../../global/helpers/global-helpers";
import { DisplaySharedDocsContent } from "./DisplaySharedDocsContent";
import { DisplaySharedDocEmptyState } from "./DisplaySharedDocEmptyState";
import useLaunchdeckModal from "../../global/hooks/use-launchdeck-modal";

function DsrSharedDocsPage({
  dsrId,
  isDsrAdmin,
  dsrTeamMembers,
  altPageNames,
  handleOpenSignupOrLoginModal,
  dsrSharedDocOutcome,
  isMobileView,
  isPublicDsrEnvironment,
  isPagePreviewMode,
  // Redux,
  dsrSharedDocs,
  deleteFilesState,
  isRoomTemplate,
  dsrTemplateId,
  isLockedRoom,
  isArchivedDsr,
  // Actions
  deleteFilesReset,
  manageSharedDoc,
  resetManageSharedDoc,
  handleChangeUserActionRequired,
}) {

  const sharedDocsRef = useRef(null);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { openLaunchdeckModal, closeLaunchdeckModal } = useLaunchdeckModal();

  const [sharedDocsArray, setSharedDocsArray] = useState("");
  const [displayEmptyState, setDisplayEmptyState] = useState(null);
  const [displaySignupPrompt, setDisplaySignupPrompt] = useState(null);

  // UX for search
  const [searchTextInput, setSearchTextInput] = useState("");

  // Sort UX
  const [showAddDocumentForm, setShowAddDocumentForm] = useState(false);
  const [showDeleteSpinner, setShowDeleteSpinner] = useState(false);
  const [showPopoverDocId, setShowPopoverDocId] = useState("");

  const [altPageName, setAltPageName] = useState("");

  useEffect(() => {
    if (dsrSharedDocOutcome) {
      resetManageSharedDoc();
    }
  }, []);

  useEffect(() => {
    if (isPublicDsrEnvironment) {
      setDisplaySignupPrompt(true);
    } else if (!!dsrSharedDocs) {
      const dsrSharedDocsInitialize = Array.isArray(dsrSharedDocs)
        ? [...dsrSharedDocs]
        : [];
      const showEmptyState = !(
        Array.isArray(dsrSharedDocsInitialize) &&
        dsrSharedDocsInitialize.length > 0
      );

      sharedDocsRef.current = dsrSharedDocsInitialize;
      setSharedDocsArray(dsrSharedDocsInitialize);
      setDisplayEmptyState(showEmptyState);
    }
  }, [dsrSharedDocs, isPublicDsrEnvironment]);

  // Auto scroll to top
  const scrollToRef = useRef(false);

  useEffect(() => {

    if (scrollToRef.current) {
      const elementPosition = scrollToRef.current.getBoundingClientRect().top;
      const offset = 200; // Offset (200px)

      window.scrollTo({
        top: elementPosition - offset, // Scroll to the position minus the offset
        // behavior: 'smooth',
      });
    }
  }, [sharedDocsArray]);

  useEffect(() => {
    if (altPageNames) {
      const { sharedDocs } = altPageNames || {};
      setAltPageName(sharedDocs)
    }
  }, [altPageNames]);

  useEffect(() => {
    if (dsrSharedDocOutcome) {
      const { manageEvent, sharedDocManaged } = dsrSharedDocOutcome || {};

      if (sharedDocManaged === true) {
        switch (manageEvent) {
          case "create-new-shared-doc": {
            setShowDeleteSpinner(false);
            toast(
              triggerLaunchdeckToast({
                useCase: "show-success-state",
                label: "Document shared",
                isMobileView,
              }),
            );
            break;
          }
          case "delete-shared-doc": {
            setShowDeleteSpinner(false);
            setShowPopoverDocId("");

            if (sharedDocManaged === true) {
              toast(
                triggerLaunchdeckToast({
                  useCase: "show-success-state",
                  label: "Document removed",
                  isMobileView,
                }),
              );
            } else {
              toast(
                triggerLaunchdeckToast({
                  useCase: "show-error-state",
                  label:
                    "Unfortunately something went wrong. Please try again later.",
                  isMobileView,
                }),
              );
            }

            break;
          }
          case "edit-shared-doc": {
            if (sharedDocManaged === true) {
              toast(
                triggerLaunchdeckToast({
                  useCase: "show-success-state",
                  label: "Edit changes saved successfully",
                  isMobileView,
                }),
              );
            } else {
              toast(
                triggerLaunchdeckToast({
                  useCase: "show-error-state",
                  label:
                    "Unfortunately something went wrong. Please try again later.",
                  isMobileView,
                }),
              );
            }

            handleDsrModalClose()
            break;
          }

          default: {
            break;
          }
        }
      }
    }
  }, [dsrSharedDocOutcome]);

  useEffect(() => {
    if (deleteFilesState) {
      // const { deleted: deletedObjects } = deleteFilesState || {}
      deleteFilesReset();
    }
  }, [deleteFilesState]);

  const handleFilterOwner = (owner) => {
    const { userId: docOwnerId } = owner || {};

    if (docOwnerId) {
      const filteredOwnerArray =
        sharedDocsArray &&
        sharedDocsArray?.filter((doc) => doc.docOwnerId === docOwnerId);
      setSharedDocsArray(filteredOwnerArray);
    } else {
      setSharedDocsArray(dsrSharedDocs);
    }
  };

  const clearSearch = () => {
    setSharedDocsArray(sharedDocsRef.current);
    // setShowClearButton(false);
    setSearchTextInput("");
  };

  const onSearchChange = (e) => {
    setSearchTextInput(e.target.value);
  };

  const handleSearch = () => {
    if (!searchTextInput) return; // Added early return

    const searchResultsArray = searchArrayofObjects(
      sharedDocsArray,
      searchTextInput,
      ["docName"],
    );

    setSharedDocsArray(searchResultsArray);
  };

  const handleDeleteSharedDoc = (deletedDocId, deletedDocFileKey) => {
    setShowDeleteSpinner(true);

    if (deletedDocFileKey) {
      deleteFiles([{ Key: deletedDocFileKey }]);
    }

    manageSharedDoc({
      dsrId: isRoomTemplate ? dsrTemplateId : dsrId,
      manageEvent: "delete-shared-doc",
      docId: deletedDocId,
    });
  };

  const handleSharedDocOrderChange = (
    activeIndex,
    overIndex,
    sharedDocsArray,
  ) => {
    const reorderedSharedDocsArray = [...sharedDocsArray];
    const [movedDoc] = reorderedSharedDocsArray.splice(activeIndex, 1);
    reorderedSharedDocsArray.splice(overIndex, 0, movedDoc);

    const reorderDocs = reorderedSharedDocsArray.reduce((acc, doc, index) => {
      const previousDisplayOrder = sharedDocsArray[index].displayOrder;
      if (doc.displayOrder !== previousDisplayOrder) {
        acc[doc.docId] = previousDisplayOrder;
      }
      return acc;
    }, {});

    setSharedDocsArray(reorderedSharedDocsArray);
    manageSharedDoc({
      dsrId: isRoomTemplate ? dsrTemplateId : dsrId,
      manageEvent: "reorder-shared-docs",
      reorderDocs,
    });
  };

  const handleEditSharedDoc = ({ docId, docName, formattedDocLink, fileKey, docDescription }) => {
    handleChangeUserActionRequired("edit-shared-doc", { editedSharedDocId: docId, editedSharedDocName: docName, editedSharedDocLink: formattedDocLink, editedSharedDocFileKey: fileKey, editedSharedDocDescription: docDescription })
  }

  const handleDsrModalClose = () => {
    closeLaunchdeckModal()
  }

  const handleShowPreviewPrompt = () => {
    openLaunchdeckModal("show-preview-page-action-modal")
  }

  const renderSharedDocContent = () => {
    if (displaySignupPrompt === true) {
      return (
        <PublicDsrSharedDocSignupPrompt
          handleOpenSignupOrLoginModal={() => handleOpenSignupOrLoginModal()}
        />
      );
    } else if (displayEmptyState === true) {
      return (
        <DisplaySharedDocEmptyState
          dsrId={isRoomTemplate ? dsrTemplateId : dsrId}
          isMobileView={isMobileView}
          showAddDocumentForm={showAddDocumentForm}
          setShowAddDocumentForm={setShowAddDocumentForm}
          displaySignupPrompt={displaySignupPrompt}
          handleOpenSignupOrLoginModal={handleOpenSignupOrLoginModal}
          isLockedRoom={isLockedRoom}
          isRoomTemplate={isRoomTemplate}
          handleShowPreviewPrompt={handleShowPreviewPrompt}
          isArchivedDsr={isArchivedDsr}
        />
      );
    } else if (displayEmptyState === false) {
      return (
        <DisplaySharedDocsContent
          dsrId={dsrId}
          isDsrAdmin={isDsrAdmin}
          sharedDocsArray={sharedDocsArray}
          searchTextInput={searchTextInput}
          clearSearch={clearSearch}
          onSearchChange={onSearchChange}
          handleSearch={handleSearch}
          isMobileView={isMobileView}
          showAddDocumentForm={showAddDocumentForm}
          handleFilterOwner={handleFilterOwner}
          handleSharedDocOrderChange={handleSharedDocOrderChange}
          dsrTeamMembers={dsrTeamMembers}
          setShowAddDocumentForm={setShowAddDocumentForm}
          showDeleteSpinner={showDeleteSpinner}
          handleDeleteSharedDoc={handleDeleteSharedDoc}
          setShowDeleteSpinner={setShowDeleteSpinner}
          showPopoverDocId={showPopoverDocId}
          setShowPopoverDocId={setShowPopoverDocId}
          isOpen={isOpen}
          onOpen={onOpen}
          onClose={onClose}
          displaySignupPrompt={displaySignupPrompt}
          handleOpenSignupOrLoginModal={handleOpenSignupOrLoginModal}
          handleEditSharedDoc={handleEditSharedDoc}
          isRoomTemplate={isRoomTemplate}
          dsrTemplateId={dsrTemplateId}
          isPagePreviewMode={isPagePreviewMode}
          handleShowPreviewPrompt={handleShowPreviewPrompt}
        />
      );
    } else return null;
  }


  return (
    <Box>
      <Box textAlign={["center", "left"]}>
        <Box
          color="brandDark.500"
          fontSize="1.5em"
          fontWeight="600"
          lineHeight="2.25em"
          ref={scrollToRef}
        >
          {altPageName || "Shared Documents"}
        </Box>
      </Box>
      {renderSharedDocContent()}
    </Box>
  )
}

function mapStateToProps(state) {
  return {
    isDsrAdmin: state.dsrAdmin.isDsrAdmin,
    dsrSharedDocs: state.currentDsrSharedDocs.dsrSharedDocs,
    dsrSharedDocOutcome: state.dsrSharedDocOutcome.results.data,
    dsrTeamMembers: state.dsrTeamMembers.members,
    isMobileView: state.mobileViewMode.isMobileView,
    isPublicDsrEnvironment: state.publicDsrEnvironment.publicDsrEnv,
    deleteFilesState: state.deleteFilesOutcome.results,
    dsrTemplateId: state.dsrTemplateId.results,
    isRoomTemplate: state.dsrTemplateId.isRoomTemplate,
    isLockedRoom: state.lockedRoomCheck.isLockedRoom,
    isArchivedDsr: state.isArchivedDsr.results
  };
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      manageSharedDoc,
      deleteFiles,
      deleteFilesReset,
      resetManageSharedDoc,
    },
    dispatch,
  );

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(DsrSharedDocsPage),
);
